/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { appDate, appMonthNameYear } from '@/utils/appDateFormat';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import Pagination from '../../../../components/Pagination';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import * as Tooltip from '@radix-ui/react-tooltip';

// Pagination configuration
const PAGE_SIZE = 12;
import UserPayroll from '../index';
import UserProfile from '../..';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  GetUserPayslips,
  DownloadPayslipPdf,
  PreviewPayslipPdf,
  BulkNotifyPayslips,
  GetUserCompanyDepartments,
  GeneratePayslipFromPayroll
} from '../../../../api/Payroll/userPayslip';
import { UserPayslipRequestInput, UserPayslipInput, UserPayslipView } from '../../../../models/payroll';
import PayslipPreviewModal from '../../../../components/payslip/PayslipPreviewModal';
import PayslipHistoryChart from './components/PayslipHistoryChart';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { parseApiDateOnly } from '@/utils/apiDate';

interface Payslip {
  id: number;
  payrollCycle: string;
  month: string;
  year: string;
  paymentDate: string;
  basicSalary: number;
  variablePay: number;
  /** Total addition-type adjustments (positive value) */
  adjustmentAdditions?: number;
  /** Total deduction-type adjustments (positive value) */
  adjustmentDeductions?: number;
  /** Net adjustments = adjustmentAdditions - adjustmentDeductions */
  adjustments?: number;
  deductions: number;
  netPay: number;
  overtimePay?: number;
  overtimeActualWage?: number | null;
  overtimeBasicWage?: number | null;
  regularOvertimeHours?: number;
  premiumOvertimeHours?: number;
  pensionDeduction?: number;
  gosiAniEmployee?: number | null;
  gosiSanedEmployee?: number | null;
  cchiEmployeeShare?: number | null;
  hourlyStatutoryBase?: number | null;
  payslipStatus: string;
  payslipUrl: string;
  employeeId: string;
  name: string;
  department?: string;
  role?: string;
  currencyCode?: string;
  includeVariablePay?: boolean;
  includeAdjustmentAdditions?: boolean;
  includeAdjustmentDeductions?: boolean;
}

interface PayslipFilters {
  year: string;
  month: string;
}

interface SelectOption {
  value: string;
  label: string;
}

type Props = Readonly<{
  component?: React.ComponentType<any> | undefined;
  role?: any;
  company?: any;
  user?: any;
  currencySymbol?: string;
}>;

const UserProfilePayslips = (props: Props) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();
  const currencySymbol = props.currencySymbol || 'AED';
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState<boolean>(false);
  const [selectedPayslip, setSelectedPayslip] = useState<Payslip | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [userDepartment, setUserDepartment] = useState<any>(null);

  // State for payslips data
  const [payslipsData, setPayslipsData] = useState<Payslip[]>([]);

  const [filters, setFilters] = useState<PayslipFilters>({
    year: '',
    month: ''
  });

  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [selectedPayslipForPreview, setSelectedPayslipForPreview] = useState<Payslip | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);

  const normalizeDepartment = (apiDepartment: any): any => {
    if (!apiDepartment) return null;

    const id = apiDepartment.id || apiDepartment.Id || apiDepartment.ID || apiDepartment.departmentId;
    const name = apiDepartment.name || apiDepartment.Name || apiDepartment.NAME || 'Unknown Department';

    if (id) {
      return {
        id: typeof id === 'string' ? parseInt(id) : id,
        name: name
      };
    }

    return null;
  };

  //getting the department of the user
  const fetchUserDepartment = async () => {
    try {
      const result = await GetUserCompanyDepartments();
      if (result.status === 200 && result.payload && result.payload.length > 0) {
        const apiDepartment = result.payload[0];
        const normalizedDepartment = normalizeDepartment(apiDepartment);

        if (normalizedDepartment) {
          setUserDepartment(normalizedDepartment);
          setDepartmentId(normalizedDepartment.id);
        } else {
          console.error("Could not normalize department data:", apiDepartment);
        }
      } else {
        console.error("No departments found in API response:", result);
      }
    } catch (error) {
      console.error('Failed to fetch user departments:', error);
      showToast(t('Failed to fetch department information'), 'error');
    }
  };

  useEffect(() => {
    let deptId = null;

    if (props.user?.departmentId) {
      deptId = props.user.departmentId;
    } else if (props.user?.department?.id) {
      deptId = props.user.department.id;
    }
    //if not in props then from api
    if (!deptId) {
      fetchUserDepartment();
    } else {
      setDepartmentId(deptId);
    }

    loadUserPayslips();
  }, [filters, id, props.user]);

  const loadUserPayslips = async () => {
    setIsLoading(true);
    try {
      const input: any = {
        pageNumber: 1,
        pageSize: 100,
        userIds: id ? [id] : []
      };

      // Add date filters if year is selected
      if (filters.year) {
        try {
          // Create dates in UTC to avoid timezone issues
          const startDate = new Date(Date.UTC(parseInt(filters.year), 0, 1)); // Jan 1
          const endDate = new Date(Date.UTC(parseInt(filters.year), 11, 31, 23, 59, 59)); // Dec 31

          // Format as ISO string without milliseconds
          input.startDate = startDate.toISOString();
          input.endDate = endDate.toISOString();

        } catch (dateError) {
          console.error("Error creating date filters:", dateError);
        }
      }

      const result = await GetUserPayslips(input);

      if (result.status === 200 && result.payload) {
        const items = result.payload.items || [];
        const departmentName = userDepartment?.name || props.user?.department?.name || '';

        const mappedData = items.map((payslip: UserPayslipView) => {
          const payrollDate = (parseApiDateOnly(payslip.payrollDate) ?? new Date(NaN));
          return {
            id: payslip.id,
            payrollCycle: appMonthNameYear(payrollDate),
            // English month NAME is the filter/sort KEY (monthOrder below), never the machine locale.
            month: payrollDate.toLocaleString('en-US', { month: 'long' }),
            year: payrollDate.getFullYear().toString(),
            paymentDate: payslip.payrollDate.toString(),
            basicSalary: payslip.basicSalary || 0,
            variablePay: payslip.variablePay || 0,
            adjustmentAdditions: payslip.adjustmentAdditions || 0,
            adjustmentDeductions: payslip.adjustmentDeductions || 0,
            adjustments: payslip.adjustments || 0,
            deductions: payslip.deductions || 0,
            netPay: payslip.netPay || 0,
            overtimePay: payslip.overtimePay || 0,
            overtimeActualWage: payslip.overtimeActualWage ?? null,
            overtimeBasicWage: payslip.overtimeBasicWage ?? null,
            regularOvertimeHours: payslip.regularOvertimeHours ?? 0,
            premiumOvertimeHours: payslip.premiumOvertimeHours ?? 0,
            pensionDeduction: payslip.pensionDeduction || 0,
            gosiAniEmployee: payslip.gosiAniEmployee ?? null,
            gosiSanedEmployee: payslip.gosiSanedEmployee ?? null,
            cchiEmployeeShare: payslip.cchiEmployeeShare ?? null,
            hourlyStatutoryBase: payslip.hourlyStatutoryBase ?? null,
            payslipStatus: payslip.status || '',
            payslipUrl: payslip.hasPdf ? String(payslip.id) : '',
            employeeId: payslip.userId || '',
            name: (payslip.user?.firstName || '') + ' ' + (payslip.user?.lastName || ''),
            department: departmentName,
            role: payslip.user?.title || '',
            currencyCode: payslip.currencyCode || undefined,
            includeVariablePay: payslip.includeVariablePay ?? true,
            includeAdjustmentAdditions: payslip.includeAdjustmentAdditions ?? true,
            includeAdjustmentDeductions: payslip.includeAdjustmentDeductions ?? true,
          };
        });
        setPayslipsData(mappedData);

        // Extract unique years and months for filter options
        const years = [...new Set(mappedData.map((p: Payslip) => p.year))].sort((a: string, b: string) => b.localeCompare(a));
        setAvailableYears(years);

        const months = [...new Set(mappedData.map((p: Payslip) => p.month))].sort((a: string, b: string) => {
          const monthOrder = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
          ];
          return monthOrder.indexOf(a) - monthOrder.indexOf(b);
        });
        setAvailableMonths(months as string[]);

        // Set default year filter to current year if it exists in available years
        if (!filters.year && years.length > 0) {
          const currentYear = new Date().getFullYear().toString();
          if (years.includes(currentYear)) {
            setFilters(prev => ({ ...prev, year: currentYear }));
          } else {
            setFilters(prev => ({ ...prev, year: years[0] }));
          }
        }
      } else {
        console.error("API returned non-200 status:", result.status, "Errors:", result.errors);
        showToast(t('Failed to load payslips') + `: ${result.errors?.[0]?.description || t('Unknown error')}`, 'error');
      }
    } catch (error: any) {
      console.error('Failed to load payslips:', error);
      console.error('Error object:', error);

      // CO-575 w1: the first three legs were dead reads on a requestHandling rejection
      // (`error.response` is never set; `error.message` is undefined on a `Result` body), so only
      // the fourth could ever fire — and it showed just the FIRST of possibly several reasons.
      // `getApiErrorMessage` reads the normalised `errors[]` off any failure shape and joins them.
      showToast(getApiErrorMessage(error, t, t('Failed to load payslips')), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayslipPreview = async (payslip: Payslip) => {
    setSelectedPayslipForPreview(payslip);
    setPreviewOpen(true);
  };

  const handlePayslipDownload = async (payslip: Payslip) => {
    try {
      await DownloadPayslipPdf(payslip.id);
      showToast(t('Payslip downloaded successfully!'), 'success');
    } catch (error) {
      console.error('Download failed:', error);
      showToast(t('Failed to download payslip. Please try again.'), 'error');
    }
  };

  const handleEmailClick = (payslip: Payslip) => {
    setSelectedPayslip(payslip);
    setEmailDialogOpen(true);
    setEmailSuccess(false);
    setEmailError('');
  };

  const handleGeneratePayslip = async (month: string, year: string) => {
    setIsGenerating(true);
    let deptId = departmentId;

    if (!deptId) {
      deptId = props.user?.departmentId || props.user?.department?.id;

      // then getting from the api
      if (!deptId) {
        try {
          const result = await GetUserCompanyDepartments();
          if (result.status === 200 && result.payload && result.payload.length > 0) {
            const apiDepartment = result.payload[0];
            const normalizedDepartment = normalizeDepartment(apiDepartment);
            if (normalizedDepartment) {
              deptId = normalizedDepartment.id;
              setDepartmentId(deptId);
            }
          }
        } catch (error) {
          console.error("Failed to fetch department:", error);
        }
      }
    }

    if (!deptId) {
      showToast(t('Department ID is required. Please ensure user has a department assigned.'), 'error');
      setIsGenerating(false);
      return;
    }

    try {
      // Convert month name to month number (0-11)
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthIndex = monthNames.indexOf(month);

      if (monthIndex === -1) {
        showToast(t('Invalid month name'), 'error');
        return;
      }

      // Create payroll date (first day of the month)
      const payrollDate = new Date(Date.UTC(parseInt(year), monthIndex, 1));

      const input: UserPayslipInput = {
        userId: id || '',
        departmentId: deptId,
        payrollDate: payrollDate,
        basicSalary: 0,
        variablePay: 0,
        deductions: 0,
        status: 'Draft',
        includeVariablePay: true,
        includeAdjustmentAdditions: true,
        includeAdjustmentDeductions: true,
        includeDeductions: true,
        includeTaxCalculations: false,
        generatePdf: true,
        sendEmailNotification: false
      };

      const result = await GeneratePayslipFromPayroll(input);

      if (result.status === 200) {
        showToast(t('Payslip generated successfully'), 'success');
        //reload after generating payslip
        setTimeout(() => {
          loadUserPayslips();
        }, 1000);
      } else {
        showToast(t('Failed to generate payslip') + ': ' + (result.errors?.[0]?.description || t('Unknown error')), 'error');
      }
    } catch (error) {
      console.error('Generation failed:', error);
      showToast(t('Failed to generate payslip'), 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFetchDepartment = async () => {
    try {
      const result = await GetUserCompanyDepartments();

      if (result.status === 200 && result.payload) {

        if (result.payload.length > 0) {
          const apiDepartment = result.payload[0];

          const normalizedDepartment = normalizeDepartment(apiDepartment);
          if (normalizedDepartment) {
            setDepartmentId(normalizedDepartment.id);
            setUserDepartment(normalizedDepartment);
            showToast(`${t('Found department')}: ${normalizedDepartment.name} (ID: ${normalizedDepartment.id})`, 'success');
          } else {
            showToast(t('Could not parse department data'), 'info');
          }
        } else {
          showToast(t('No departments returned from API'), 'info');
        }
      } else {
        showToast(`${t('API returned status')}: ${result.status}`, 'info');
      }
    } catch (error) {
      console.error('Failed to fetch department:', error);
      showToast(t('Failed to fetch department'), 'error');
    }
  };

  const yearOptions: SelectOption[] = [
    { value: '', label: t('All Years') },
    ...availableYears.map(year => ({ value: year, label: year }))
  ];

  const monthOptions: SelectOption[] = [
    { value: '', label: t('All Months') },
    ...availableMonths.map(month => ({ value: month, label: t(`common:${month}`, month) }))
  ];

  const handleFilterChange = (field: keyof PayslipFilters, value: string): void => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
  };

  const getFilteredPayslips = (): Payslip[] => {
    return payslipsData.filter(payslip => {
      const yearMatch = !filters.year || payslip.year === filters.year;
      const monthMatch = !filters.month || payslip.month === filters.month;
      return yearMatch && monthMatch;
    });
  };

  // Get paginated payslips
  const getPaginatedPayslips = (): Payslip[] => {
    const filtered = getFilteredPayslips();
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filtered.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const getTotalPages = (): number => {
    const filtered = getFilteredPayslips();
    return Math.ceil(filtered.length / PAGE_SIZE);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getStatusBadge = (status: string): string => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-medium";

    switch (status.toLowerCase()) {
      case 'generated':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300`;
      case 'sent':
      case 'notified':
        return `${baseClasses} bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300`;
      case 'pending':
        return `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300`;
      case 'error':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300`;
      case 'draft':
        return `${baseClasses} bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300`;
      default:
        return `${baseClasses} bg-muted text-muted-foreground`;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'generated':
        return <Icon name="CheckCircle" size={14} className="text-blue-600 dark:text-blue-400" />;
      case 'sent':
      case 'notified':
        return <Icon name="Mail" size={14} className="text-purple-600 dark:text-purple-400" />;
      case 'pending':
        return <Icon name="Clock" size={14} className="text-orange-600 dark:text-orange-400" />;
      case 'draft':
        return <Icon name="Edit" size={14} className="text-cyan-600 dark:text-cyan-400" />;
      case 'error':
        return <Icon name="AlertCircle" size={14} className="text-red-600 dark:text-red-400" />;
      default:
        return <Icon name="FileText" size={14} className="text-muted-foreground" />;
    }
  };

  const getStatusDisplayText = (status: string): string => {
    // so sent and notified are the same for consistency
    return status.toLowerCase() === 'sent' ? t('Notified') : t(status);
  };

  const filteredPayslips = getFilteredPayslips();
  const paginatedPayslips = getPaginatedPayslips();
  const totalPages = getTotalPages();

  return (
    <>
      <UserProfile>
        <UserPayroll>
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-medium text-foreground">{t('Payslips History')}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('View and manage your historical payslips')}
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-sm text-muted-foreground">
                  <Icon name="FileText" size={16} className="me-2" />
                  <span>{filteredPayslips.length} {t('payslips available')}</span>
                </div>
                {!departmentId && (
                  <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                    {t('User department not found. Please assign a department before generating payslips.')}
                  </div>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="bg-muted rounded-xl p-6 border border-border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Filter" size={20} className="text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">{t('Filter Payslips')}</h3>
                </div>
                <button
                  onClick={() => {
                    setFilters({
                      year: '',
                      month: ''
                    });
                  }}
                  className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  {t('Clear Filters')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label={t('Year')}
                  options={yearOptions}
                  value={filters.year}
                  onChange={(value: any) => handleFilterChange('year', value ?? '')}
                />
                <Select
                  label={t('Month')}
                  options={monthOptions}
                  value={filters.month}
                  onChange={(value: any) => handleFilterChange('month', value ?? '')}
                />
              </div>
            </div>

            {/* History Chart */}
            {filteredPayslips.length > 1 && (
              <PayslipHistoryChart payslips={filteredPayslips} currencySymbol={currencySymbol} />
            )}

            {/* Payslips Table */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="px-6 py-4 border-b border-border bg-muted">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-foreground">{t('Payslip Records')}</h3>
                  <div className="text-sm text-muted-foreground">
                    {t('Total')}: {filteredPayslips.length} {t('records')}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('Pay Period')}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('Payment Date')}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('Gross Salary')}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('Variable Pay')}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('Adjustments')}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('Total')}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('Status')}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('Actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <svg className="animate-spin h-6 w-6 text-primary mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <p className="mt-2 text-sm text-muted-foreground">{t('Loading payslips...')}</p>
                        </td>
                      </tr>
                    ) : filteredPayslips.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <Icon name="FileText" size={48} className="text-muted-foreground/40 mb-3" />
                            <p className="text-muted-foreground text-sm">{t('No payslips found for the selected filters')}</p>
                            <Button
                              variant="link"
                              size="sm"
                              className="mt-2"
                              onClick={() => {
                                setFilters({
                                  year: '',
                                  month: ''
                                });
                                setCurrentPage(1);
                              }}
                            >
                              {t('Clear filters')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedPayslips.map((payslip) => (
                        <tr key={payslip.id} className="hover:bg-muted">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-foreground">
                              {payslip.payrollCycle}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {appDate(payslip.paymentDate)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {payslip.currencyCode || currencySymbol} {payslip.basicSalary.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {payslip.currencyCode || currencySymbol} {(payslip.variablePay || 0).toLocaleString()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(payslip.adjustments ?? 0) !== 0 ? (
                              <span className={`text-sm font-medium ${(payslip.adjustments ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(payslip.adjustments ?? 0) >= 0 ? '+' : ''}{payslip.currencyCode || currencySymbol} {(payslip.adjustments ?? 0).toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">&mdash;</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-semibold text-foreground">
                              {payslip.currencyCode || currencySymbol} {(payslip.netPay || 0).toLocaleString()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getStatusIcon(payslip.payslipStatus)}
                              <span className={getStatusBadge(payslip.payslipStatus) + " ms-2"}>
                                {getStatusDisplayText(payslip.payslipStatus)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-1">
                              {/* Preview is available for all statuses */}
                              <Tooltip.Provider>
                                <Tooltip.Root>
                                  <Tooltip.Trigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={t('Preview') as string}
                                      onClick={() => handlePayslipPreview(payslip)}
                                    >
                                      <Icon name="Eye" size={16} />
                                    </Button>
                                  </Tooltip.Trigger>
                                  <Tooltip.Portal>
                                    <Tooltip.Content className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md border border-border" sideOffset={5}>
                                      {t('Preview')}
                                    </Tooltip.Content>
                                  </Tooltip.Portal>
                                </Tooltip.Root>
                              </Tooltip.Provider>

                              {/* Download is available for Generated, Notified statuses */}
                              {(['Generated', 'Notified'].includes(payslip.payslipStatus)) && (
                                <Tooltip.Provider>
                                  <Tooltip.Root>
                                    <Tooltip.Trigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label={t('Download') as string}
                                        onClick={() => handlePayslipDownload(payslip)}
                                      >
                                        <Icon name="Download" size={16} />
                                      </Button>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                      <Tooltip.Content className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md border border-border" sideOffset={5}>
                                        {t('Download')}
                                      </Tooltip.Content>
                                    </Tooltip.Portal>
                                  </Tooltip.Root>
                                </Tooltip.Provider>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination/Summary */}
              {filteredPayslips.length > 0 && (
                <div className="px-6 py-3 border-t border-border bg-muted">
                  <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      {t('Showing')} {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, filteredPayslips.length)} {t('of')} {filteredPayslips.length} {t('payslips')}
                    </span>
                    {totalPages > 1 && (
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    )}
                    <span className="text-sm text-muted-foreground">
                      {t('Total')}: {currencySymbol} {
                        filteredPayslips.reduce((sum, payslip) => sum + (payslip.netPay || 0), 0)
                          .toLocaleString()
                      }
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <ToastRenderer toast={toast} onDismiss={dismissToast} />
        </UserPayroll>
      </UserProfile>

      {/* Preview Modal */}
      <PayslipPreviewModal
        isOpen={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setSelectedPayslipForPreview(null);
        }}
        payslip={selectedPayslipForPreview ? {
          id: selectedPayslipForPreview.id,
          name: selectedPayslipForPreview.name,
          employeeId: selectedPayslipForPreview.employeeId,
          department: selectedPayslipForPreview.department,
          role: selectedPayslipForPreview.role,
          basicSalary: selectedPayslipForPreview.basicSalary,
          variablePay: selectedPayslipForPreview.variablePay,
          deductions: selectedPayslipForPreview.deductions,
          netPay: selectedPayslipForPreview.netPay,
          adjustments: selectedPayslipForPreview.adjustments,
          adjustmentAdditions: selectedPayslipForPreview.adjustmentAdditions,
          adjustmentDeductions: selectedPayslipForPreview.adjustmentDeductions,
          overtimePay: selectedPayslipForPreview.overtimePay,
          overtimeActualWage: selectedPayslipForPreview.overtimeActualWage,
          overtimeBasicWage: selectedPayslipForPreview.overtimeBasicWage,
          regularOvertimeHours: selectedPayslipForPreview.regularOvertimeHours,
          premiumOvertimeHours: selectedPayslipForPreview.premiumOvertimeHours,
          pensionDeduction: selectedPayslipForPreview.pensionDeduction,
          gosiAniEmployee: selectedPayslipForPreview.gosiAniEmployee,
          gosiSanedEmployee: selectedPayslipForPreview.gosiSanedEmployee,
          cchiEmployeeShare: selectedPayslipForPreview.cchiEmployeeShare,
          hourlyStatutoryBase: selectedPayslipForPreview.hourlyStatutoryBase,
          includeVariablePay: selectedPayslipForPreview.includeVariablePay,
          includeAdjustmentAdditions: selectedPayslipForPreview.includeAdjustmentAdditions,
          includeAdjustmentDeductions: selectedPayslipForPreview.includeAdjustmentDeductions,
          payslipStatus: selectedPayslipForPreview.payslipStatus,
          payslipUrl: selectedPayslipForPreview.payslipUrl,
          payrollDate: selectedPayslipForPreview.paymentDate,
          payrollCycle: selectedPayslipForPreview.payrollCycle,
        } : null}
        payrollCycle={selectedPayslipForPreview?.payrollCycle || ''}
        currencySymbol={selectedPayslipForPreview?.currencyCode || currencySymbol}
        onDownload={async (payslipId) => {
          await DownloadPayslipPdf(payslipId);
          showToast(t('Payslip downloaded successfully!'), 'success');
        }}
        onEmail={async (payslipId) => {
          const result = await BulkNotifyPayslips({ payslipIds: [payslipId] });
          if (result.status === 200 && !result.errors?.length) {
            showToast(t('Notification sent successfully'), 'success');
            setTimeout(() => loadUserPayslips(), 1000);
          } else {
            showToast(result.errors?.[0]?.description || t('Failed to send notification'), 'error');
          }
        }}
      />
    </>
  );
};

const mapStateToProps = (state: any) => {
  const effectiveCurrency = state.user?.userRules?.currency;
  const currencySymbol = effectiveCurrency?.currencyCode || effectiveCurrency?.currencySymbol || 'AED';
  return {
    role: state.user.role,
    company: state.user.company,
    user: state.user,
    currencySymbol,
  };
};

export default connect(mapStateToProps)(UserProfilePayslips);
